import React from 'react';

import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useMobileLayout from 'Hooks/useMobileLayout';
import MetaQueryFieldGroup
  from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/Content/ManagedAccountPageTabPanel/Layouts/BalanceAndAllocationTabPanelLayout/ManagedAccountActivitySummary/MetaQueryFieldGroup';
import fr from 'facet-rest';
import { DATE_RANGE_CUSTOM } from 'Consts/dateRange';
import ManagedAccountPageInfoStat
  from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/Content/ManagedAccountPageInfoBar/ManagedAccountPageInfoStat';
import {
  percentageFormatter,
} from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/Content/ManagedAccountPageTabPanel/Layouts/useChartDataFromResource';
import useManagedAccountPageEventLogger, {
  logMetaQueryFieldGroupChange,
} from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/managedAccountPage.eventLogging';
import PerformanceChartContainer
  from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/Content/ManagedAccountPageTabPanel/Layouts/AccountPerformanceDashboard/AccountPerformance/AccountPerformanceChartLegend/PerformanceChartContainer';
import { useSnackbarContext } from 'Context/SnackbarContext';

const useStyles = buildUseStyles('AccountPerformance', (theme) => ({
  topBar: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: ({ isMobile }) => (isMobile ? 'column' : 'row'),
    gap: theme.spacing(2),
    width: '100%',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  info: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    alignItems: 'center',
  },
}));

const metaQueryOverride = ({ resource, metaQueryValues }) => fr.metaOverride().name('activitySummaryMetaQueryOverrideSet')
  .overrideAll({
    hidden: true,
  }, { ordinal: 200 })
  .nameOverride('datePeriod', {
    hidden: false,
    default: resource.link('self')?.param('datePeriod') || DATE_RANGE_CUSTOM,
    required: metaQueryValues.datePeriod !== DATE_RANGE_CUSTOM,
    exclude: metaQueryValues.datePeriod === DATE_RANGE_CUSTOM,
  }, { ordinal: 201 })
  .nameOverride('startDate', {
    hidden: metaQueryValues.datePeriod !== DATE_RANGE_CUSTOM,
    default: resource.attr('startingDate'),
    required: metaQueryValues.datePeriod === DATE_RANGE_CUSTOM,
    exclude: metaQueryValues.datePeriod !== DATE_RANGE_CUSTOM,
  }, { ordinal: 201 })
  .nameOverride('endDate', {
    hidden: metaQueryValues.datePeriod !== DATE_RANGE_CUSTOM,
    default: resource.attr('endingDate'),
    required: metaQueryValues.datePeriod === DATE_RANGE_CUSTOM,
    exclude: metaQueryValues.datePeriod !== DATE_RANGE_CUSTOM,
  }, { ordinal: 201 });

const getPortfolioReturn = (resource) => {
  return resource.attr('accountPerformance') !== undefined ? percentageFormatter(resource.attr('accountPerformance')) : null;
};

const shouldShowBenchmarkReturn = (showBenchmark, portfolioReturn, performanceDifference) => {
  return showBenchmark && portfolioReturn && performanceDifference;
};

const getTitle = (resource, portfolioReturn, showBenchmark) => {
  let title = 'Your Portfolio Return';
  if (shouldShowBenchmarkReturn(showBenchmark, portfolioReturn, resource.attr('performanceDifference'))) {
    title += ' (vs benchmark)';
  }
  return title;
};

const getPortfolioReturnWithDiff = (resource, showBenchmark) => {
  const portfolioReturn = getPortfolioReturn(resource);
  if (shouldShowBenchmarkReturn(showBenchmark, portfolioReturn, resource.attr('performanceDifference'))) {
    return `${portfolioReturn} (${Number(resource.attr('performanceDifference')) > 0 ? '+' : ''}${percentageFormatter(resource.attr('performanceDifference'))})`;
  }
  return portfolioReturn;
};

export default function AccountPerformance({
  benchmarkedPerformance, setBenchmarkedPerformance, showBenchmark, toggleShowBenchmark,
}) {
  const { displayErrorMessage } = useSnackbarContext();

  const isMobile = useMobileLayout();
  const classes = useStyles({ isMobile });

  async function handleNewQueryLink(link) {
    try {
      const [data] = await link.get();
      setBenchmarkedPerformance(data);
    } catch (e) {
      displayErrorMessage(e, (e.errors && e.errors[0]?.detail) || 'There was a problem loading the chart data.');
    }
  }

  const portfolioReturn = getPortfolioReturnWithDiff(benchmarkedPerformance, showBenchmark);
  const title = getTitle(benchmarkedPerformance, portfolioReturn, showBenchmark);

  const { logPerformanceFiltersEvent } = useManagedAccountPageEventLogger();

  function logPerformanceControlChange(values, field, newValue) {
    logMetaQueryFieldGroupChange(values, field, newValue, logPerformanceFiltersEvent);
  }

  const renderBenchmark = benchmarkedPerformance.attr('benchmarkPerformance') && showBenchmark;

  return <>
    <Box width="100%" height="100%" display="flex" flexDirection="column">
      <Box className={classes.topBar}>
        <MetaQueryFieldGroup
          resource={benchmarkedPerformance}
          metaQueryName={fr.queries.getBenchmarkedPerformance.name}
          metaQueryOverride={metaQueryOverride}
          onNewLink={handleNewQueryLink}
          onValueChange={logPerformanceControlChange}
          test="account-performance-filters"
        />
        <Box className={classes.info}>
          {portfolioReturn ? <ManagedAccountPageInfoStat title={title} value={portfolioReturn} /> : null}
          {renderBenchmark ? <ManagedAccountPageInfoStat title="Benchmark Returns" value={percentageFormatter(benchmarkedPerformance.attr('benchmarkPerformance'))} /> : null}
        </Box>
      </Box>

      <PerformanceChartContainer
        benchmarkedPerformance={benchmarkedPerformance}
        showBenchmark={showBenchmark}
        toggleShowBenchmark={toggleShowBenchmark}
      />
    </Box>
  </>;
}
